import { useRef, type ReactNode } from "react";

import { cx } from "class-variance-authority";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Rectangle,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import { useElementSize } from "../../hooks/useElementSize";
import { rawRadius, rawSpacing } from "../../styles/theme.css";
import {
    AXIS_CHAR_WIDTH,
    buildAxisTickLabels,
    getLabelStride,
} from "../../utils/chartAxis";

import * as theme from "./StackedBarTrendChart.css";
import { StackedBarTrendChartTooltip } from "./StackedBarTrendChartTooltip";
import {
    StackedBarTrendChartBar,
    StackedBarTrendChartBucket,
    StackedBarTrendChartSeries,
} from "./types";

const BUCKET_LABEL_KEY = "stackedBarTrendChartBucketLabel";

const CHART_MARGIN = {
    top: rawSpacing[4],
    right: rawSpacing[2],
    bottom: 0,
    left: 0,
};
const Y_AXIS_WIDTH = 44;
const Y_AXIS_TICK_COUNT = 3;
// Figma's seven-bucket plot uses 58% of each band; single/year bars cap at 120px.
const MAX_BAR_SIZE = 120;
const BAR_WIDTH_RATIO = 0.58;

export interface StackedBarTrendChartProps {
    /** Supplied in bottom-to-top order. */
    series: StackedBarTrendChartSeries[];
    data: StackedBarTrendChartBucket[];
    height?: number;
    /** Never called for a zero-value segment. */
    onBarClick?: (
        bucketIndex: number,
        series: StackedBarTrendChartSeries
    ) => void;
    renderTooltip?: (bar: StackedBarTrendChartBar) => ReactNode;
}

/** One vertical stack per bucket, with independently interactive segments. */
export function StackedBarTrendChart({
    series,
    data,
    height = 220,
    onBarClick,
    renderTooltip,
}: StackedBarTrendChartProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const { width } = useElementSize(containerRef);
    const rows = data.map((bucket) => ({
        [BUCKET_LABEL_KEY]: bucket.label,
        ...Object.fromEntries(
            series.map(({ key }) => [key, bucket.values[key] ?? 0])
        ),
    }));
    const maxValue = Math.max(
        0,
        ...data.map((bucket) =>
            series.reduce((sum, { key }) => sum + (bucket.values[key] ?? 0), 0)
        )
    );
    const yAxisMax = maxValue === 0 ? Y_AXIS_TICK_COUNT - 1 : "auto";
    const labels = data.map(({ label }) => label);
    const plotWidth = Math.max(
        0,
        (width ?? 0) - Y_AXIS_WIDTH - CHART_MARGIN.right
    );
    const bucketWidth = data.length > 0 ? plotWidth / data.length : plotWidth;
    const labelStride = getLabelStride(labels, bucketWidth);
    const barSize = Math.min(MAX_BAR_SIZE, bucketWidth * BAR_WIDTH_RATIO);

    return (
        <div
            className={cx(
                theme.container,
                onBarClick !== undefined && theme.clickable
            )}
            ref={containerRef}
        >
            <ResponsiveContainer width="100%" height={height}>
                <BarChart data={rows} margin={CHART_MARGIN} barSize={barSize}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                        dataKey={BUCKET_LABEL_KEY}
                        type="category"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        ticks={buildAxisTickLabels(labels, labelStride, [
                            data.length - 1,
                        ])}
                        tick={
                            <StackedBarTrendChartAxisTick
                                plotRight={width ?? 0}
                            />
                        }
                    />
                    <YAxis
                        width={Y_AXIS_WIDTH}
                        domain={[0, yAxisMax]}
                        tickCount={Y_AXIS_TICK_COUNT}
                        allowDecimals={false}
                        axisLine={false}
                        tickLine={false}
                        className={theme.axisTick}
                    />
                    <Tooltip
                        shared={false}
                        content={
                            <StackedBarTrendChartTooltip
                                series={series}
                                renderContent={renderTooltip}
                            />
                        }
                    />
                    {series.map((entry, seriesIndex) => (
                        <Bar
                            key={entry.key}
                            dataKey={entry.key}
                            name={entry.label}
                            fill={entry.color}
                            stackId="stack"
                            shape={
                                <StackedBarTrendChartSegment
                                    series={series}
                                    data={data}
                                    seriesIndex={seriesIndex}
                                />
                            }
                            onClick={
                                onBarClick === undefined
                                    ? undefined
                                    : (_bar: unknown, index: number) => {
                                          if (
                                              (data[index]?.values[entry.key] ??
                                                  0) > 0
                                          ) {
                                              onBarClick(index, entry);
                                          }
                                      }
                            }
                            isAnimationActive={false}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

interface SegmentProps {
    series: StackedBarTrendChartSeries[];
    data: StackedBarTrendChartBucket[];
    seriesIndex: number;
    index?: number;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    fill?: string;
}

function StackedBarTrendChartSegment({
    series,
    data,
    seriesIndex,
    index = 0,
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    fill,
}: SegmentProps) {
    if (height <= 0 || width <= 0) {
        return <g />;
    }
    const hasAbove = series
        .slice(seriesIndex + 1)
        .some(({ key }) => (data[index]?.values[key] ?? 0) > 0);
    const hasBelow = series
        .slice(0, seriesIndex)
        .some(({ key }) => (data[index]?.values[key] ?? 0) > 0);
    // Separation and corner radii shrink for tiny counts.
    const seam = hasAbove ? Math.min(2, height / 4) : 0;
    const radius = Math.min(rawRadius.sm, width / 2, (height - seam) / 2);
    return (
        <g>
            <rect
                data-pendo={series[seriesIndex]?.["data-pendo"]}
                x={x}
                y={y}
                width={width}
                height={height}
                fill="transparent"
            />
            {hasBelow ? (
                <path
                    transform={`translate(${x}, ${y + seam})`}
                    d={[
                        `M 0 ${radius}`,
                        `Q 0 0 ${radius} 0`,
                        `H ${width - radius}`,
                        `Q ${width} 0 ${width} ${radius}`,
                        `V ${height - seam + radius}`,
                        // Concave corners follow the rounded top of the segment below.
                        `Q ${width} ${height - seam} ${width - radius} ${height - seam}`,
                        `H ${radius}`,
                        `Q 0 ${height - seam} 0 ${height - seam + radius}`,
                        "Z",
                    ].join(" ")}
                    fill={fill}
                    pointerEvents="none"
                />
            ) : (
                <Rectangle
                    x={x}
                    y={y + seam}
                    width={width}
                    height={height - seam}
                    fill={fill}
                    radius={[radius, radius, 0, 0]}
                    pointerEvents="none"
                />
            )}
        </g>
    );
}

interface AxisTickProps {
    plotRight: number;
    x?: number;
    y?: number;
    payload?: { value?: string };
}

function StackedBarTrendChartAxisTick({
    plotRight,
    x = 0,
    y = 0,
    payload,
}: AxisTickProps) {
    const label = payload?.value ?? "";
    const halfWidth = (label.length * AXIS_CHAR_WIDTH) / 2;
    const clampedX = Math.min(Math.max(x, halfWidth), plotRight - halfWidth);
    return (
        <text
            x={clampedX}
            y={y}
            dy={14}
            textAnchor="middle"
            className={theme.axisTickHorizontal}
        >
            {label}
        </text>
    );
}
